import { Component, Injectable, ElementRef, inject, viewChild, OnInit } from "@angular/core";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { Observable } from "rxjs";
import { LoadingService } from "../../shared/services/loading-overlay/loading-overlay.component";
import { VendorCard } from "./types/VendorCard";
import { VendorSelectionService } from "./vendor-selection-modal.service";
import { TypeaheadComponent, TypeaheadItem } from "../../components/typeahead/typeahead.component";
import { VendorCountry } from "./types/VendorModalDropdowns";
import { PaginatorService } from '../../shared/services/paginator.service';
import { PagedResults } from "../../shared/interfaces/PagedResults";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { FormsModule } from "@angular/forms";
import { YesNoPipe } from "../../shared/pipes/yes-no.pipe";

export interface ShowVendorSelectionModalData {
  countryId?: number;
  activeContactOnly?: boolean;
  activeVendorOnly?: boolean;
  checkAgreementStatus: boolean;
}

type VendorCardDisplayData = VendorCard & {
  IsAgreementStatusOkay: boolean;
  DoesAgreementExpireSoon: boolean;
};

@Component({
  selector: 'vendor-selection-modal',
  templateUrl: 'vendor-selection-modal.component.html',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatButtonModule, MatIconModule, TypeaheadComponent,
    MatProgressSpinnerModule, MatCardModule, MatPaginatorModule, MatDividerModule, YesNoPipe],
  providers: [LoadingService, VendorSelectionService]
})
export class VendorSelectionModalComponent implements OnInit {
  private paginatorService = inject(PaginatorService);
  private dialogRef = inject<MatDialogRef<VendorSelectionModal>>(MatDialogRef);
  private loadingService = inject(LoadingService);
  private showModalData = inject<ShowVendorSelectionModalData>(MAT_DIALOG_DATA);
  private vendorService = inject(VendorSelectionService);

  private TodaysDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  private ExpireThresholdDate: Date = new Date(this.TodaysDate.getFullYear(), this.TodaysDate.getMonth(), this.TodaysDate.getDate() + 90);

  protected searchResults: VendorCardDisplayData[] = [];
  protected searchValueVendor: string;
  protected searchValueContact: string;
  protected SelectedCountryId: number;
  protected pageIndex: number = 0;
  protected totalCount: number = 0;
  protected itemsPerPage = this.paginatorService.getItemsPerPageDefault();
  protected pageSizeOptions = this.paginatorService.getPageSizeOptions();
  protected isLoading: boolean = false;
  protected checkAgreementStatus: boolean;
  protected DialogTitle: string = "Vendor - Contact Search";
  protected AgreementExpiresSoonMessage = "Agreement with this Vendor is going to expire in less than 90 days. Communicate with payment admins to extend this agreement for next growing season";

  // @ViewChild('vendorSearchInput', { static: false }) vendorSearchInput: ElementRef<HTMLInputElement>;
  private vendorSearchInput = viewChild.required<ElementRef<HTMLInputElement>>('vendorSearchInput');

  protected Dropdowns = {
    Countries: <Array<TypeaheadItem<number>>>[],
    CountriesById: <{ [countryId: number]: VendorCountry }>{},
  }

  ngOnInit() {
    const loadRef = this.loadingService.StartWaiting("Loading Current Vendors...");
    this.vendorService.GetVendorCountries().subscribe((countries) => {
      this.Dropdowns.Countries = countries.map((x) => <TypeaheadItem<number>>{
        Item: x.CountryId,
        DisplayText: x.CountryName
      });

      this.checkAgreementStatus = this.showModalData.checkAgreementStatus;

      if (this.showModalData.countryId != null)
        this.SelectedCountryId = this.showModalData.countryId;

      loadRef.Stop();
      // The autoFocusDirective doesn't persist when a loading dialog pops up
      // so manually set focus to the search input if vendors were passed in
      setTimeout(() => {
        this.vendorSearchInput().nativeElement.focus();
      }, 250);
    });
  }

  protected onPageChanged(e: PageEvent) {
    this.itemsPerPage = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.UpdateItems();
  }

  protected Search() {
    this.pageIndex = 0;
    this.totalCount = 0;
    this.UpdateItems();
  }

  private UpdateItems() {
    this.searchResults = [];
    this.isLoading = true;

    this.vendorService.GetVendorSearch({
      VendorName: this.searchValueVendor,
      ContactName: this.searchValueContact,
      CountryIds: [this.SelectedCountryId],
      ActiveContactOnly: this.showModalData.activeContactOnly,
      ActiveVendorOnly: this.showModalData.activeVendorOnly,
      IsDescending: false,
      ItemsPerPage: this.itemsPerPage,
      Page: this.pageIndex,
      OrderBy: "",
      VendorCode: null,
      VendorZip: "",
      VendorActive: true,
      TrialNontrialNumber:""
    })
      .subscribe((c: PagedResults<VendorCard>) => {
        this.searchResults = c.Rows.map((vendor) => {
          var tmp: VendorCardDisplayData = <any>vendor;

          tmp.DoesAgreementExpireSoon = false;

          if (tmp.AgreementEndDate != null) {
            tmp.AgreementEndDate = new Date(tmp.AgreementEndDate);
            tmp.DoesAgreementExpireSoon = tmp.AgreementEndDate < this.ExpireThresholdDate;
          }

          tmp.IsAgreementStatusOkay = this.IsVendorAgreementStatusOkay(tmp);

          return tmp;
        });
        this.totalCount = c.TotalCount;
        this.isLoading = false;
      });
  }

  private IsVendorAgreementStatusOkay(vendor: VendorCard) {
    if (vendor.AgreementRequired == false)
      return true;

    if (vendor.AgreementEndDate === null || vendor.AgreementEndDate === undefined)
      return false;

    return vendor.AgreementEndDate >= this.TodaysDate;
  }

  protected ClearContactSearch() {
    this.searchValueContact = "";
  }

  protected ClearVendorSearch() {
    this.searchValueVendor = "";
  }

  protected AddVendor(row: VendorCard) {
    this.dialogRef.close(row);
  }

  protected Cancel() {
    this.dialogRef.close(undefined);
  }
}

@Injectable()
export class VendorSelectionModal {
  private dialog = inject(MatDialog);

  /** Displays passed in Vendor and allows for searching other Vendors
   * @returns {Observable<VendorCard | undefined>} The selected Vendors or undefined if no Vendors were selected.
   */
  public ShowModal(data: ShowVendorSelectionModalData): Observable<VendorCard | undefined> {
    const ref = this.dialog.open(VendorSelectionModalComponent, {
      data: data,
      width: "900px",
      disableClose: true,
      autoFocus: false,
    });

    return ref.afterClosed();
  }
}
